import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { PageHeader, EmptyState } from '../components/Editorial';
import Button from '../components/Button';
import SharedReportPromptModal from '../components/SharedReportPromptModal';

// Recruiting-status pill on a coach report card. Commitment (green) supersedes
// the offer count (gold, matching the report PDF's offers accent).
const REC_PILL = (committed) => ({
  display: 'inline-block', marginTop: 6, padding: '2px 10px', borderRadius: 12,
  background: committed ? '#1E7A46' : '#BC8A3D', color: '#fff',
  fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em',
});

export default function CoachReportsPage() {
  const { API_URL } = useAuth();
  const navigate = useNavigate();

  const [opened, setOpened] = useState([]);
  const [codeInput, setCodeInput] = useState('');
  const [active, setActive] = useState(null); // {title, sport, players}
  const [error, setError] = useState('');
  const [opening, setOpening] = useState(false);

  const loadReceived = useCallback(async () => {
    const cfg = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    try {
      const res = await axios.get(`${API_URL}/reports/received`, cfg);
      setOpened(res.data || []);
    } catch (e) { /* non-fatal */ }
  }, [API_URL]);

  useEffect(() => { loadReceived(); }, [loadReceived]);

  const openReport = async (code) => {
    setError('');
    setOpening(true);
    const cfg = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    try {
      const res = await axios.post(`${API_URL}/reports/${code}/open`, {}, cfg);
      setActive(res.data);
      loadReceived();
    } catch (e) {
      setError(e.response?.data?.detail || 'Report not found.');
    }
    setOpening(false);
  };

  return (
    <div>
      <SharedReportPromptModal onPreview={openReport} onResolvedAny={loadReceived} />
      <PageHeader title="Reports" sub="Open a report a coordinator shared with you, or revisit one you've already opened." />

      <div style={{ display: 'flex', gap: 8, margin: '12px 0', flexWrap: 'wrap' }}>
        <input placeholder="Enter report code" value={codeInput}
          onChange={(e) => setCodeInput(e.target.value.trim().toUpperCase())}
          style={{ padding: 8, minWidth: 200 }} aria-label="Report code" />
        <Button variant="primary" onClick={() => codeInput && openReport(codeInput)} loading={opening}>
          Open report
        </Button>
      </div>
      {error && <div className="cn-inline-msg cn-inline-msg--err">{error}</div>}

      {active && (
        <div style={{ margin: '16px 0' }}>
          <h3>{active.title}</h3>
          <div className="results-grid">
            {active.players.map((p) => (
              <div key={p.user_id} className="player-card" role="button" tabIndex={0}
                onClick={() => navigate(`/athletes/${p.user_id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(`/athletes/${p.user_id}`); }}>
                {p.primary_photo && <img src={p.primary_photo} alt="" className="player-card__photo" />}
                <div className="player-card__body">
                  <div className="player-card__name">{p.name}</div>
                  <div className="player-card__detail">
                    Class of {p.graduation_year || 'N/A'} · {p.position_primary || ''}
                  </div>
                  {p.committed ? (
                    <span style={REC_PILL(true)}>
                      Committed{p.committed_college_name ? ` · ${p.committed_college_name}` : ''}
                    </span>
                  ) : (p.offers_count > 0 && (
                    <span style={REC_PILL(false)}>
                      {p.offers_count} Offer{p.offers_count !== 1 ? 's' : ''}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <h3 style={{ marginTop: 24 }} data-testid="shared_lists_section">Shared with me</h3>
      {opened.length === 0 ? (
        <EmptyState title="No shared lists yet" body="Paste a code above to open one, or accept a shared list when prompted." />
      ) : (
        <div>
          {opened.map((r) => (
            <div key={r.code} className="sidebar-athlete-item" data-testid="shared_list_row"
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
              onClick={() => openReport(r.code)}>
              {r.shared && !r.opened && (
                <span aria-label="New" title="New" style={{ width: 8, height: 8,
                  borderRadius: '50%', background: '#2563EB', flex: 'none' }} />
              )}
              <span><strong>{r.title}</strong> · {r.athlete_count} athlete(s) · {r.sport}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
